import { inject } from '@angular/core';
import { SiteBrandingService } from '../../../../services/site-branding.service';
import { MediaService } from '../../../../services/media.service';
import { switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  AccountRole,
  PasswordField,
  PasswordForm
} from '../../../../models/app.models';

import { AuthService } from '../../../../auth/auth.service';

import {
  ContactSettings,
  SiteSettingsService
} from '../../../../services/site-setting.service';

import {
  EmailSettings,
  EmailSettingsService
} from '../../../../services/email-settings.service';

import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-admin-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-settings.component.html',
  styleUrls: [
    './admin-settings.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSettingsComponent implements OnInit {
  readonly branding = inject(SiteBrandingService);
  private readonly mediaService = inject(MediaService);
  brandingSaving = false;

  uploadLogo(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.brandingSaving) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      this.toastService.error('Choose a JPEG, PNG, or WebP image up to 5 MB.');
      input.value = '';
      return;
    }
    this.brandingSaving = true;
    this.mediaService.uploadImage(file).pipe(
      switchMap(asset => this.siteSettingService.updateBranding(asset.id)),
      switchMap(() => this.siteSettingService.getBranding()),
    ).subscribe({
      next: settings => {
        this.branding.apply(settings);
        this.brandingSaving = false;
        input.value = '';
        this.toastService.success('Site logo updated.');
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.brandingSaving = false;
        input.value = '';
        this.toastService.error('Unable to update the site logo. Please try again.');
        this.changeDetector.markForCheck();
      },
    });
  }

  resetLogo(): void {
    if (this.brandingSaving) return;
    this.brandingSaving = true;
    this.siteSettingService.updateBranding(null).pipe(
      switchMap(() => this.siteSettingService.getBranding()),
    ).subscribe({
      next: settings => {
        this.branding.apply(settings);
        this.brandingSaving = false;
        this.toastService.success('Default site logo restored.');
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.brandingSaving = false;
        this.toastService.error('Unable to reset the site logo. Please try again.');
        this.changeDetector.markForCheck();
      },
    });
  }

  // ============================================================
  // SITE SETTINGS
  // ============================================================

  contactSettings: ContactSettings = {
    contactEmail: '',
    resumeEmail: '',
    donationEmail: '',
    contactPhone: '',
    contactAddress: ''
  };

  siteSettingsLoading = true;
  siteSettingsSaving = false;

  // ============================================================
  // EMAIL DELIVERY SETTINGS
  // ============================================================

  emailSettings: EmailSettings = {
    senderEmail: '',
    hasAppPassword: false
  };

  // The saved App Password is never loaded from the API.
  // This only contains a newly entered replacement credential.
  emailAppPassword = '';

  emailSettingsLoading = true;
  emailSettingsSaving = false;

  // ============================================================
  // PASSWORD SETTINGS
  // ============================================================

  readonly accountRoles: AccountRole[] = [
    'Admin',
    'Manager',
    'Crew'
  ];

  expandedPasswordRole: AccountRole | null = null;

  passwordForms: Record<AccountRole, PasswordForm> = {
    Admin: this.emptyPasswordForm(),
    Manager: this.emptyPasswordForm(),
    Crew: this.emptyPasswordForm()
  };

  visiblePasswordFields: Record<string, boolean> = {};

  passwordErrors: Record<AccountRole, string> = {
    Admin: '',
    Manager: '',
    Crew: ''
  };

  constructor(
    private readonly authService: AuthService,
    private readonly siteSettingService: SiteSettingsService,
    private readonly emailService: EmailSettingsService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadContactSettings();
    this.loadEmailSettings();
  }

  // ============================================================
  // SITE SETTINGS
  // ============================================================

  loadContactSettings(): void {
    this.siteSettingsLoading = true;

    this.siteSettingService
      .getContactSettings()
      .subscribe({
        next: settings => {
          this.contactSettings = {
            contactEmail: settings.contactEmail ?? '',
            resumeEmail: settings.resumeEmail ?? '',
            donationEmail: settings.donationEmail ?? '',
            contactPhone: settings.contactPhone ?? '',
            contactAddress: settings.contactAddress ?? ''
          };

          this.siteSettingsLoading = false;
          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            'Unable to load site settings:',
            error
          );

          this.siteSettingsLoading = false;

          this.toastService.error(
            'Unable to load website settings.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  saveContactSettings(): void {
    if (
      !this.contactSettings.contactEmail.trim() ||
      !this.contactSettings.resumeEmail.trim() ||
      !this.contactSettings.donationEmail.trim()
    ) {
      this.toastService.error(
        'Complete all required email fields.'
      );

      return;
    }

    if (
      !this.isValidEmail(
        this.contactSettings.contactEmail
      ) ||
      !this.isValidEmail(
        this.contactSettings.resumeEmail
      ) ||
      !this.isValidEmail(
        this.contactSettings.donationEmail
      )
    ) {
      this.toastService.error(
        'Enter valid email addresses.'
      );

      return;
    }

    this.siteSettingsSaving = true;

    const settings: ContactSettings = {
      contactEmail:
        this.contactSettings.contactEmail.trim(),
      resumeEmail:
        this.contactSettings.resumeEmail.trim(),
      donationEmail:
        this.contactSettings.donationEmail.trim(),
      contactPhone:
        this.contactSettings.contactPhone.trim(),
      contactAddress:
        this.contactSettings.contactAddress.trim()
    };

    this.siteSettingService
      .updateContactSettings(settings)
      .subscribe({
        next: response => {
          this.contactSettings = settings;
          this.siteSettingsSaving = false;

          this.toastService.success(
            response.message
          );

          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            'Unable to save site settings:',
            error
          );

          this.siteSettingsSaving = false;

          this.toastService.error(
            error.error?.message ??
              'Unable to save website settings.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  // ============================================================
  // EMAIL DELIVERY SETTINGS
  // ============================================================

  loadEmailSettings(): void {
    this.emailSettingsLoading = true;

    this.emailService
      .getSettings()
      .subscribe({
        next: settings => {
          this.emailSettings = {
            senderEmail:
              settings.senderEmail ?? '',
            hasAppPassword:
              settings.hasAppPassword
          };

          // We never receive or retain the saved credential.
          this.emailAppPassword = '';

          this.emailSettingsLoading = false;
          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            'Unable to load email settings:',
            error
          );

          this.emailSettingsLoading = false;

          this.toastService.error(
            'Unable to load website email settings.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  saveEmailSettings(): void {
    const senderEmail =
      this.emailSettings.senderEmail.trim();

    if (!senderEmail) {
      this.toastService.error(
        'Enter the website sender email.'
      );

      return;
    }

    if (!this.isValidEmail(senderEmail)) {
      this.toastService.error(
        'Enter a valid website sender email.'
      );

      return;
    }

    if (
      !this.emailSettings.hasAppPassword &&
      !this.emailAppPassword.trim()
    ) {
      this.toastService.error(
        'Enter the Gmail App Password.'
      );

      return;
    }

    this.emailSettingsSaving = true;

    // Google commonly displays App Passwords grouped with spaces.
    // Remove whitespace before sending it to the API.
    const appPassword =
      this.emailAppPassword
        .replace(/\s+/g, '')
        .trim();

    this.emailService
      .updateSettings({
        senderEmail,
        appPassword:
          appPassword || null
      })
      .subscribe({
        next: () => {
          this.emailSettings = {
            senderEmail,
            hasAppPassword:
              this.emailSettings.hasAppPassword ||
              Boolean(appPassword)
          };

          // Never retain the credential in the browser
          // after it has successfully been saved.
          this.emailAppPassword = '';

          this.emailSettingsSaving = false;

          this.toastService.success(
            'Website email settings saved.'
          );

          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            'Unable to save email settings:',
            error
          );

          this.emailSettingsSaving = false;

          this.toastService.error(
            error.error?.message ??
              'Unable to save website email settings.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  // ============================================================
  // PASSWORD SETTINGS
  // ============================================================

  togglePasswordPanel(
    role: AccountRole
  ): void {
    this.expandedPasswordRole =
      this.expandedPasswordRole === role
        ? null
        : role;

    this.passwordErrors[role] = '';
  }

  togglePasswordVisibility(
    role: AccountRole,
    field: PasswordField
  ): void {
    const key =
      `${role}-${field}`;

    this.visiblePasswordFields[key] =
      !this.visiblePasswordFields[key];
  }

  isPasswordVisible(
    role: AccountRole,
    field: PasswordField
  ): boolean {
    const key =
      `${role}-${field}`;

    return Boolean(
      this.visiblePasswordFields[key]
    );
  }

  savePassword(
    role: AccountRole
  ): void {
    const form =
      this.passwordForms[role];

    this.passwordErrors[role] = '';

    if (
      !form.currentPassword ||
      !form.newPassword ||
      !form.confirmPassword
    ) {
      this.passwordErrors[role] =
        'Complete all three password fields.';

      return;
    }

    if (
      form.newPassword !==
      form.confirmPassword
    ) {
      this.passwordErrors[role] =
        'The new passwords do not match.';

      return;
    }

    if (
      form.newPassword.length < 5
    ) {
      this.passwordErrors[role] =
        'Use at least 5 characters for the new password.';

      return;
    }

    if (
      form.currentPassword ===
      form.newPassword
    ) {
      this.passwordErrors[role] =
        'The new password must be different from the current password.';

      return;
    }

    this.authService
      .updatePassword(
        role,
        form.currentPassword,
        form.newPassword
      )
      .subscribe({
        next: response => {
          this.passwordForms[role] =
            this.emptyPasswordForm();

          this.visiblePasswordFields = {};
          this.expandedPasswordRole = null;

          this.toastService.success(
            response.message
          );

          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            `Unable to update ${role} password:`,
            error
          );

          const message =
            error.error?.message ??
            `Unable to update ${role} password.`;

          this.passwordErrors[role] =
            message;

          this.toastService.error(
            message
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  // ============================================================
  // HELPERS
  // ============================================================

  private isValidEmail(
    email: string
  ): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      .test(email.trim());
  }

  private emptyPasswordForm():
    PasswordForm {
    return {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
  }
}
